import type { EditorView } from '@codemirror/view';

const TAB = '    ';
const SELF_CLOSING_TAG = /<(img|br|hr|input|meta|link)\s+([^]*?)\/>/gs;
const SPACE_AROUND_PUNCTUATION = /\s+([,;:.!?])\s+/g;
const SPACE_AFTER_OPEN_BRACKET = /([([])\s+/g;
const SPACE_BEFORE_CLOSE_BRACKET = /\s+([)\]])/g;
const LIQUID_TOKEN = /(\{%[^%]*%\}|\{\{[^}]*\}\})/g;
const BLOCK_TAG = /\{%[^%]*%\}/;
const OPENING_TAG = /^\{%\s*(?!end)\w+\s/;
const CLOSING_TAG = /^\{%\s*end\w+\s*%\}$/;
const MIDDLE_TAG = /^\{%\s*(else|elsif|elseif|when)\s/;
const PUNCTUATION_ONLY = /^[,;:.!?-]+$/;

const normalizeLiquid = (token: string): string =>
  token
    .replace(/\{%\s*/g, '{% ')
    .replace(/\s*%\}/g, ' %}')
    .replace(/\{\{\s*/g, '{{ ')
    .replace(/\s*\}\}/g, ' }}');

const splitLine = (line: string): string[] => {
  const baseIndent = line.match(/^(\s*)/)?.[1] ?? '';
  const trimmed = line.trim();
  if (!trimmed) return [];

  // Only split when a block tag ({% %}) is present; otherwise keep inline
  if (!BLOCK_TAG.test(trimmed)) return [baseIndent + normalizeLiquid(trimmed)];

  const tokens = trimmed
    .split(LIQUID_TOKEN)
    .map((t) => t.trim())
    .filter(Boolean);
  const result: string[] = [];

  for (const token of tokens) {
    if (PUNCTUATION_ONLY.test(token) && result.length > 0) {
      result[result.length - 1] += token;
    } else {
      result.push(baseIndent + normalizeLiquid(token));
    }
  }

  return result;
};

const indentLines = (lines: string[]): string[] => {
  let depth = 0;
  return lines.map((line) => {
    const baseIndent = line.match(/^(\s*)/)?.[1] ?? '';
    const content = line.slice(baseIndent.length);

    const isClosing = CLOSING_TAG.test(content);
    const isMiddle = MIDDLE_TAG.test(content);
    const isOpening = OPENING_TAG.test(content);

    if (isClosing || isMiddle) depth = Math.max(0, depth - 1);
    const indented = baseIndent + TAB.repeat(depth) + content;
    if (isOpening || isMiddle) depth += 1;
    return indented;
  });
};

const formatLiquidSyntax = (html: string): string => {
  const cleaned = html
    .replace(SELF_CLOSING_TAG, (_match, tag, attrs) => `<${tag} ${attrs.replace(/\s+/g, ' ').trim()} />`)
    .replace(SPACE_AROUND_PUNCTUATION, '$1 ')
    .replace(SPACE_AFTER_OPEN_BRACKET, '$1')
    .replace(SPACE_BEFORE_CLOSE_BRACKET, '$1');

  const lines = indentLines(cleaned.split('\n').flatMap(splitLine));
  return lines.length ? `${lines.join('\n')}\n` : '';
};

/**
 * Format HTML document in the CodeMirror editor using Prettier.
 *
 * NOTE: This uses Prettier's browser standalone bundle for client-side HTML formatting.
 * This is separate from our build-time formatter (oxfmt) and is required for runtime
 * formatting in the HTML editor component.
 */
export const formatDocument = async (view: EditorView): Promise<void> => {
  const code = view.state.doc.toString();
  const cursorOffset = view.state.selection.main.head;

  const prettierModule = await import('prettier/standalone');
  const htmlPluginModule = await import('prettier/plugins/html');

  const prettier = prettierModule.default || prettierModule;
  const prettierHtmlPlugin = htmlPluginModule.default || htmlPluginModule;

  const { formatted, cursorOffset: newOffset } = await prettier.formatWithCursor(code, {
    cursorOffset,
    parser: 'html',
    plugins: [prettierHtmlPlugin],
    printWidth: 200,
    tabWidth: 4,
    bracketSameLine: true,
    htmlWhitespaceSensitivity: 'ignore',
    singleAttributePerLine: false,
    proseWrap: 'preserve',
  });

  const finalFormatted = formatLiquidSyntax(formatted);

  if (finalFormatted === code) return;

  const { scrollDOM } = view;
  const { scrollTop } = scrollDOM;

  view.dispatch({
    changes: { from: 0, to: view.state.doc.length, insert: finalFormatted },
    selection: { anchor: newOffset },
  });

  scrollDOM.scrollTop = scrollTop;
};
